import React, { useState, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'

import {
    useAppDispatch,
    useDateRange,
    useSelectVendorService,
    useUser,
} from '../../../hooks'

import {
    UpdateWorkOrderRequest,
    WorkorderDetailActionThunk,
    deleteWorkorder,
    getOrCreateWorkorderChannel,
    getMessageList,
    setMessageList,
} from '../../../store'

import {
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    Switch,
    useTheme,
    Badge,
    Tooltip,
} from '@material-ui/core'
import MessageIcon from '@material-ui/icons/Message'
import MenuOpenIcon from '@material-ui/icons/MenuOpen'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import {
    Container,
    MultiSelector,
    NumberInput,
    Selector,
    SideDrawer,
    StatusBadge,
    TransitionLog,
    WorkorderTransitionButtons,
    WorkorderTransitionLogPopver,
} from '../../../components'
import {
    Area,
    AreaConfig,
    getTotalPriceExpense,
    getUiString,
    IdentifiableNamedObject,
    ListVendor,
    ModelMap,
    Unit,
    UnitConfig,
    BaseWorkorder,
    WorkorderStatus,
    isWorkorderSafe,
    EDIT_WORKORDERS,
    DELETE_WORKORDERS,
    SEE_VENDOR_FINANCES,
    ASSIGN_WORKORDER_WALKER,
    Schedule,
    activeStatuses,
    Service,
    PermissionRole,
} from '../../../models'

import { axiosInstance, fromPickerDate, toPickerDate } from '../../../helpers'
import NumberFormat from 'react-number-format'
import { hasPermission } from '../../../models/Users/services'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { ReactComponent as AddChangeOrder } from '../../../assets/icons/add-change-order.svg'
import { CreateChangeOrder } from './CreateChangeOrder'
import { DateTime } from 'luxon'
import { useTimezone } from '../../../contexts/timezone'
import { typographyStyle } from '../../../theme/typographyStyle'

interface ServiceAreaUIState {
    [areaId: number]: {
        include: boolean
    }
}

interface ServiceAreaProps {
    area: Area
    areaConfig?: AreaConfig
    checked: boolean
    locked: boolean
    onChange: () => void
}

const ServiceArea = (props: ServiceAreaProps) => {
    const { area, checked, onChange, locked, areaConfig } = props
    return (
        <FormControlLabel
            control={
                <Checkbox
                    color="primary"
                    checked={checked}
                    onChange={onChange}
                    disabled={locked}
                />
            }
            label={
                <div>
                    <span>
                        {areaConfig?.name} ({area.area_label})
                    </span>
                </div>
            }
        />
    )
}

interface Props {
    workorder?: BaseWorkorder
    unit?: Unit
    unitConfig?: UnitConfig
    vendorList?: ListVendor[]
    serviceList: Service[]
    areaConfigMap: ModelMap<AreaConfig>
    onClose: () => void
    openMessages: () => void
    updateWorkorder: (req: UpdateWorkOrderRequest) => WorkorderDetailActionThunk
    schedule?: Schedule
    permissionRoles: PermissionRole[]
}

// const DRAWER_WIDTH = DEFAULT_DRAWER_WIDTH
const DRAWER_WIDTH = 330
const TITLE_HEIGHT = 66
const WORKORDER_RANGE_KEY = 'WORKORDER_RANGE_KEY'

export const WorkorderDetailDrawer = (props: Props) => {
    const {
        workorder,
        unit,
        onClose,
        openMessages,
        updateWorkorder,
        unitConfig,
        areaConfigMap,
        schedule,
        serviceList,
    } = props

    const theme = useTheme()
    const { workspaceUser } = useUser()
    const { currentTimezone } = useTimezone()
    const { dateRange, setRange, setStartDate, setEndDate } = useDateRange({
        rangeKey: WORKORDER_RANGE_KEY,
        color: theme.palette.primary.main,
        useNoon: true,
    })

    // When workorder has no start/end dates, use local state so pickers show empty instead of hook's default "today"
    const [emptyWorkorderDates, setEmptyWorkorderDates] = useState<{
        start: Date | null
        end: Date | null
    }>({ start: null, end: null })

    const [addPrice, setAddPrice] = useState<number | undefined>()
    const [customPrice, setCustomPrice] = useState<number | null>(null)
    const [priority, setPriority] = useState(false)
    const [approvalRoleIds, setApprovalRoleIds] = useState<number[]>([])

    const unitAreas =
        unit?.areas.filter(
            (area) => areaConfigMap[area.area_config]?.shows_on_schedule,
        ) ?? []

    const [vendor, setVendor] = useState({
        id: workorder?.vendor_id ?? -1,
        name: workorder?.vendor_name ?? 'unknown',
    })

    const [selectedServiceId, setSelectedServiceId] = useState<number>(-1)

    const dispatch = useAppDispatch()

    const [addChangeOrder, setAddChangeOrder] = useState(false)

    const [
        serviceAreaUIState,
        setServiceAreaUIState,
    ] = useState<ServiceAreaUIState>({})

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    const popOverOpen = Boolean(anchorEl)

    const closePopover = () => {
        setAnchorEl(null)
    }

    const openPopover = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const workorderPickerRange = useMemo(() => {
        if (!workorder?.start_date || !workorder?.end_date) {
            return null
        }
        return {
            startDate: toPickerDate(
                DateTime.fromISO(workorder.start_date).setZone(currentTimezone),
            ),
            endDate: toPickerDate(
                DateTime.fromISO(workorder.end_date).setZone(currentTimezone),
            ),
        }
    }, [workorder?.start_date, workorder?.end_date, currentTimezone])

    useEffect(() => {
        dispatch(setMessageList([]))
        if (workorder) {
            if (isWorkorderSafe(workorder)) {
                setPriority(workorder.priority)
                setVendor({
                    id: workorder.vendor_id,
                    name: workorder.vendor_name,
                })
                if (workorderPickerRange) {
                    setRange({
                        ...workorderPickerRange,
                        key: WORKORDER_RANGE_KEY,
                        color: theme.palette.primary.main,
                    })
                } else {
                    setEmptyWorkorderDates({ start: null, end: null })
                }
            }

            const newSaUiState: ServiceAreaUIState = {}
            unitAreas.forEach((area) => {
                newSaUiState[area.id] = {
                    include: false,
                }
            })
            workorder.service_area_list.map((sa) => {
                newSaUiState[sa.area_id] = {
                    include: true,
                }
            })
            setServiceAreaUIState(newSaUiState)
            setCustomPrice(workorder.custom_price)
            setAddPrice(workorder.add_price)
            setApprovalRoleIds(workorder.approval_role_ids ?? [])

            dispatch(getOrCreateWorkorderChannel(workorder.id)).then((res) => {
                dispatch(getMessageList({ channelId: res.data.id }))
            })

            if (workorder.service_id) {
                setSelectedServiceId(workorder.service_id)
            }
        } else {
            const now = toPickerDate(DateTime.now().setZone(currentTimezone))
            setVendor({ id: -1, name: '' })
            setPriority(false)
            setRange({
                startDate: now,
                endDate: now,
                key: WORKORDER_RANGE_KEY,
                color: theme.palette.primary.main,
            })
            setAddPrice(0)
            setCustomPrice(null)
            setServiceAreaUIState({})
            setSelectedServiceId(-1)
            setApprovalRoleIds([])
        }
        // TODO: get channel details + workorder messages
    }, [workorder, unit])

    // Re-anchor the pickers when the user switches between property/user time
    useEffect(() => {
        if (!workorderPickerRange) {
            return
        }
        setRange({
            ...workorderPickerRange,
            key: WORKORDER_RANGE_KEY,
            color: theme.palette.primary.main,
        })
    }, [currentTimezone])

    let vendorOptions: IdentifiableNamedObject[] = []
    if (workorder?.price_locked && isWorkorderSafe(workorder)) {
        vendorOptions = [
            { id: workorder.vendor_id, name: workorder.vendor_name },
        ]
    } else if (props.vendorList) {
        vendorOptions = props.vendorList.filter((vnd) => {
            return vnd.services.find((srv) => srv.id === workorder?.service_id)
        })
    }

    let messageBadgeCount = 0
    if ((workorder?.message_count ?? 0) > 0) {
        messageBadgeCount = workorder!.message_count
    }
    if ((workorder?.unread_count ?? 0) > 0) {
        messageBadgeCount = workorder!.unread_count ?? 0
    }

    const CANNOT_EDIT_WORKORDER = !hasPermission(workspaceUser, EDIT_WORKORDERS)

    const goodServiceList = useMemo(() => {
        if (vendor.id === -1) {
            return serviceList
        } else {
            return (
                props.vendorList?.find((vnd) => vnd.id === vendor.id)
                    ?.services ?? serviceList
            )
        }
    }, [serviceList, vendor])

    return (
        <SideDrawer
            width={DRAWER_WIDTH}
            open={workorder !== undefined}
            handleClose={onClose}
            title={
                <Container
                    style={{
                        flexDirection: 'column',
                        width: '100%',
                        margin: theme.spacing(1),
                        maxHeight: TITLE_HEIGHT,
                        minHeight: TITLE_HEIGHT,
                    }}
                >
                    <Container
                        style={{
                            flex: 1,
                            alignItems: 'center',
                            ...typographyStyle(theme.typography.h6),
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 700,
                            }}
                        >
                            {workorder?.service_name}
                        </span>
                        {hasPermission(workspaceUser, SEE_VENDOR_FINANCES) && (
                            <NumberFormat
                                prefix="$"
                                thousandSeparator
                                value={
                                    workorder
                                        ? getTotalPriceExpense(workorder)
                                        : 0
                                }
                                displayType="text"
                                style={{
                                    marginLeft: theme.spacing(2),
                                    fontWeight:
                                        theme.typography.fontWeightLight,
                                }}
                            />
                        )}
                        <div style={{ flex: 1 }} />
                        <StatusBadge
                            text={getUiString(
                                workorder?.status ?? WorkorderStatus.ASSIGNED,
                            )}
                            customStyle={{
                                container: {
                                    backgroundColor:
                                        theme.palette[
                                            workorder?.status ?? 'primary'
                                        ].main,
                                    marginLeft: theme.spacing(1),
                                },
                            }}
                        />
                    </Container>

                    <Container
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 300,
                                textOverflow: 'ellipsis',
                                maxWidth: DRAWER_WIDTH / 2,
                                overflow: 'hidden',
                                direction: 'rtl',
                                textAlign: 'left',
                            }}
                        >
                            {unit?.folder.path}
                            {unit?.folder.name}
                        </span>
                        <span
                            style={{
                                fontWeight: 700,
                                marginLeft: theme.spacing(2),
                            }}
                        >
                            {unit?.name}
                        </span>
                        <span
                            style={{
                                fontWeight: 500,
                                marginLeft: theme.spacing(2),
                            }}
                        >
                            {unitConfig?.name ?? 'unknown'}
                        </span>
                    </Container>
                </Container>
            }
        >
            {workorder === undefined ? (
                <div />
            ) : (
                <>
                    <Container scrollY direction="column">
                        <Container style={{ width: '100%' }}>
                            <Selector
                                label="Service"
                                data={goodServiceList}
                                searchable
                                currentValue={selectedServiceId}
                                onChange={(e) => {
                                    setSelectedServiceId(
                                        e.target.value as number,
                                    )
                                }}
                                getDisplayString={(service) => service.name}
                                customStyle={{
                                    formControl: {
                                        margin: theme.spacing(1),
                                        flex: 1,
                                    },
                                }}
                                disabled={
                                    workorder.price_locked ||
                                    CANNOT_EDIT_WORKORDER ||
                                    !schedule?.active
                                }
                            />
                        </Container>

                        <Container
                            style={{
                                padding: theme.spacing(1),
                                alignItems: 'center',
                            }}
                        >
                            <Selector
                                customStyle={{
                                    formControl: {
                                        margin: theme.spacing(1, 0),
                                        flex: 1,
                                    },
                                }}
                                disabled={
                                    workorder.price_locked ||
                                    CANNOT_EDIT_WORKORDER ||
                                    !schedule?.active
                                }
                                currentValue={vendor.id}
                                label="Vendor"
                                data={vendorOptions}
                                getDisplayString={(v) => v.name}
                                onChange={(e) => {
                                    const vendorId = Number(e.target.value)
                                    if (typeof vendorId === 'number') {
                                        const newVendor = props.vendorList?.find(
                                            (vnd) => vnd.id === vendorId,
                                        )
                                        if (newVendor !== undefined) {
                                            setVendor({
                                                id: newVendor.id,
                                                name: newVendor.name,
                                            })
                                        }
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={priority}
                                        color="primary"
                                        onChange={() => setPriority(!priority)}
                                        disabled={
                                            CANNOT_EDIT_WORKORDER ||
                                            !schedule?.active ||
                                            workorder.price_locked
                                        }
                                    />
                                }
                                labelPlacement="top"
                                label="Priority"
                            />

                            <IconButton onClick={openMessages}>
                                <Badge
                                    badgeContent={messageBadgeCount}
                                    color={
                                        (workorder.unread_count ?? 0) > 0
                                            ? 'secondary'
                                            : 'primary'
                                    }
                                >
                                    <MessageIcon fontSize="large" />
                                </Badge>
                            </IconButton>
                        </Container>

                        <Container style={{ padding: theme.spacing(1) }}>
                            <KeyboardDatePicker
                                value={
                                    workorder?.start_date && workorder?.end_date
                                        ? dateRange.startDate ?? null
                                        : emptyWorkorderDates.start
                                }
                                onChange={(date) => {
                                    const d = date ?? new Date()
                                    if (
                                        workorder?.start_date &&
                                        workorder?.end_date
                                    ) {
                                        setStartDate(d)
                                    } else {
                                        setEmptyWorkorderDates((prev) => {
                                            const newStart = d
                                            const newEnd = prev.end ?? d
                                            setRange({
                                                startDate: newStart,
                                                endDate: newEnd,
                                                key: WORKORDER_RANGE_KEY,
                                                color:
                                                    theme.palette.primary.main,
                                            })
                                            return {
                                                start: newStart,
                                                end: newEnd,
                                            }
                                        })
                                    }
                                }}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                label="Start Date"
                                disabled={
                                    !schedule?.active || workorder.price_locked
                                }
                            />

                            <KeyboardDatePicker
                                value={
                                    workorder?.start_date && workorder?.end_date
                                        ? dateRange.endDate ?? null
                                        : emptyWorkorderDates.end
                                }
                                onChange={(date) => {
                                    const d = date ?? new Date()
                                    if (
                                        workorder?.start_date &&
                                        workorder?.end_date
                                    ) {
                                        setEndDate(d)
                                    } else {
                                        setEmptyWorkorderDates((prev) => {
                                            const newStart = prev.start ?? d
                                            const newEnd = d
                                            setRange({
                                                startDate: newStart,
                                                endDate: newEnd,
                                                key: WORKORDER_RANGE_KEY,
                                                color:
                                                    theme.palette.primary.main,
                                            })
                                            return {
                                                start: newStart,
                                                end: newEnd,
                                            }
                                        })
                                    }
                                }}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                style={{ marginLeft: theme.spacing(1) }}
                                label="End Date"
                                disabled={
                                    !schedule?.active || workorder.price_locked
                                }
                            />
                        </Container>
                        <Divider />

                        <Container style={{ padding: theme.spacing(1) }}>
                            <FormGroup>
                                {unitAreas
                                    .slice(0, unitAreas.length / 2)
                                    .map((area) => {
                                        return (
                                            <ServiceArea
                                                key={`WO_DETAIL_SA_${area.id}`}
                                                locked={
                                                    workorder.price_locked ||
                                                    CANNOT_EDIT_WORKORDER ||
                                                    !schedule?.active
                                                }
                                                area={area}
                                                areaConfig={
                                                    areaConfigMap[
                                                        area.area_config
                                                    ]
                                                }
                                                checked={
                                                    serviceAreaUIState[area.id]
                                                        ?.include === true
                                                }
                                                onChange={() =>
                                                    setServiceAreaUIState({
                                                        ...serviceAreaUIState,
                                                        [area.id]: {
                                                            include: !serviceAreaUIState[
                                                                area.id
                                                            ].include,
                                                        },
                                                    })
                                                }
                                            />
                                        )
                                    })}
                            </FormGroup>

                            <FormGroup>
                                {unitAreas
                                    .slice(unitAreas.length / 2)
                                    .map((area) => {
                                        return (
                                            <ServiceArea
                                                key={`WO_DETAIL_SA_${area.id}`}
                                                locked={
                                                    workorder.price_locked ||
                                                    CANNOT_EDIT_WORKORDER ||
                                                    !schedule?.active
                                                }
                                                area={area}
                                                areaConfig={
                                                    areaConfigMap[
                                                        area.area_config
                                                    ]
                                                }
                                                checked={
                                                    serviceAreaUIState[area.id]
                                                        ?.include === true
                                                }
                                                onChange={() =>
                                                    setServiceAreaUIState({
                                                        ...serviceAreaUIState,
                                                        [area.id]: {
                                                            include: !serviceAreaUIState[
                                                                area.id
                                                            ].include,
                                                        },
                                                    })
                                                }
                                            />
                                        )
                                    })}
                            </FormGroup>
                        </Container>
                        {hasPermission(workspaceUser, SEE_VENDOR_FINANCES) && (
                            <Container style={{ padding: theme.spacing(1) }}>
                                <NumberInput
                                    prefix={'$'}
                                    variant="outlined"
                                    label="Add Price"
                                    value={addPrice}
                                    disabled={
                                        CANNOT_EDIT_WORKORDER ||
                                        !schedule?.active ||
                                        workorder.price_locked
                                    }
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            setAddPrice(undefined)
                                        } else {
                                            setAddPrice(Number(e.target.value))
                                        }
                                    }}
                                    style={{ marginRight: theme.spacing(2) }}
                                />

                                <NumberInput
                                    prefix={'$'}
                                    variant="outlined"
                                    label="Custom Price"
                                    value={customPrice}
                                    disabled={
                                        CANNOT_EDIT_WORKORDER ||
                                        !schedule?.active ||
                                        workorder.price_locked
                                    }
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            setCustomPrice(null)
                                        } else {
                                            setCustomPrice(
                                                Number(e.target.value),
                                            )
                                        }
                                    }}
                                />
                            </Container>
                        )}

                        {/* Approval Role Selector */}
                        {hasPermission(
                            workspaceUser,
                            ASSIGN_WORKORDER_WALKER,
                        ) && (
                            <Container
                                style={{
                                    padding: theme.spacing(1),
                                    flexDirection: 'column',
                                }}
                            >
                                <Container
                                    style={{
                                        alignItems: 'center',
                                        marginBottom: theme.spacing(1),
                                    }}
                                >
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h6,
                                            ),
                                            fontWeight: 700,
                                        }}
                                    >
                                        Approval Roles
                                    </span>
                                    <Tooltip
                                        title={
                                            <span style={{ fontSize: 12 }}>
                                                Only users with these permission
                                                roles can Approve this
                                                workorder. If left empty, anyone
                                                with approval permission can
                                                approve it.
                                            </span>
                                        }
                                    >
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            color="action"
                                            style={{
                                                marginLeft: theme.spacing(1),
                                            }}
                                        />
                                    </Tooltip>
                                </Container>
                                <MultiSelector
                                    label="Roles that can approve this workorder"
                                    keyIdentifier="WO_DETAIL_APPROVAL_ROLES"
                                    options={props.permissionRoles}
                                    selectedOptions={approvalRoleIds}
                                    onChange={setApprovalRoleIds}
                                    disabled={
                                        CANNOT_EDIT_WORKORDER ||
                                        !schedule?.active
                                    }
                                    style={{
                                        formControl: {
                                            width: '100%',
                                        },
                                    }}
                                />
                            </Container>
                        )}
                    </Container>

                    <Divider />

                    <Container
                        style={{
                            padding: theme.spacing(1),
                            flexDirection: 'column',
                        }}
                    >
                        <Container style={{ alignItems: 'center' }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                }}
                            >
                                Last Transition
                            </span>

                            <div style={{ flex: 1 }} />

                            <Tooltip title="View All">
                                <IconButton onClick={openPopover}>
                                    <MenuOpenIcon fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </Container>

                        {/* Display the most recent transition */}
                        {workorder.transition_logs.length > 0 && (
                            <TransitionLog
                                log={
                                    workorder.transition_logs[
                                        workorder.transition_logs.length - 1
                                    ]
                                }
                                theme={theme}
                            />
                        )}
                    </Container>

                    <Divider />

                    <WorkorderTransitionButtons
                        workorder={workorder}
                        user={workspaceUser}
                        disabled={!schedule?.active}
                    />

                    {activeStatuses.includes(workorder.status) && (
                        <Container
                            style={{
                                padding: theme.spacing(1),
                                flexDirection: 'column',
                            }}
                        >
                            <Container style={{ alignItems: 'center' }}>
                                <span
                                    style={{
                                        ...typographyStyle(theme.typography.h6),
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                    }}
                                >
                                    Add Change Order
                                </span>

                                <Container
                                    style={{
                                        marginLeft: theme.spacing(2),
                                        alignItems: 'center',
                                    }}
                                >
                                    <IconButton
                                        onClick={() => setAddChangeOrder(true)}
                                    >
                                        <AddChangeOrder />
                                    </IconButton>
                                </Container>
                            </Container>
                            {addChangeOrder && (
                                <CreateChangeOrder
                                    setAddChangeOrder={setAddChangeOrder}
                                    unitId={workorder.unit_id}
                                    workorder={workorder}
                                    onClose={onClose}
                                />
                            )}
                        </Container>
                    )}

                    {/* Footer */}
                    <div style={{ flex: 1 }} />
                    <Divider />
                    <Container
                        style={{
                            padding: theme.spacing(2),
                        }}
                    >
                        <Button
                            color="secondary"
                            variant="outlined"
                            style={{ marginRight: theme.spacing(2) }}
                            disabled={
                                !hasPermission(
                                    workspaceUser,
                                    DELETE_WORKORDERS,
                                ) || !schedule?.active
                            }
                            onClick={() => {
                                if (
                                    confirm(
                                        'Caution! This will permanently delete this workorder.',
                                    )
                                ) {
                                    axiosInstance
                                        .delete(`workorder/${workorder.id}/`)
                                        .then(() => {
                                            dispatch(
                                                deleteWorkorder(workorder.id),
                                            )
                                            toast.success('Workorder Deleted')
                                            props.onClose()
                                        })
                                }
                            }}
                        >
                            Delete
                        </Button>
                        <div style={{ flex: 1 }} />
                        <Button
                            variant="outlined"
                            style={{ marginRight: theme.spacing(2) }}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            variant="outlined"
                            disabled={
                                CANNOT_EDIT_WORKORDER ||
                                !schedule?.active ||
                                workorder.price_locked
                            }
                            onClick={() => {
                                const serviceAreas = Object.keys(
                                    serviceAreaUIState,
                                ).reduce<number[]>((prev, areaId) => {
                                    const aid = parseInt(areaId)
                                    if (serviceAreaUIState[aid].include) {
                                        return prev.concat(aid)
                                    }
                                    return prev
                                }, [])

                                const effectiveStartDate =
                                    workorder.start_date && workorder.end_date
                                        ? dateRange.startDate
                                        : emptyWorkorderDates.start
                                const effectiveEndDate =
                                    workorder.start_date && workorder.end_date
                                        ? dateRange.endDate
                                        : emptyWorkorderDates.end

                                // Workorders span whole days in the property's
                                // timezone, matching how the assign modal
                                // creates them.
                                const startDateIso = effectiveStartDate
                                    ? fromPickerDate(
                                          effectiveStartDate,
                                          currentTimezone,
                                      )
                                          .startOf('day')
                                          .toUTC()
                                          .toISO()
                                    : null
                                const endDateIso = effectiveEndDate
                                    ? fromPickerDate(
                                          effectiveEndDate,
                                          currentTimezone,
                                      )
                                          .endOf('day')
                                          .toUTC()
                                          .toISO()
                                    : null

                                const updateWoReq: UpdateWorkOrderRequest = {
                                    workorderId: workorder.id,
                                    body: {
                                        add_price: addPrice,
                                        start_date: startDateIso ?? undefined,
                                        end_date: endDateIso ?? undefined,
                                        service_areas: serviceAreas,
                                        priority: priority,
                                        vendor: vendor.id,
                                        service: selectedServiceId,
                                    },
                                }

                                if (
                                    hasPermission(
                                        workspaceUser,
                                        ASSIGN_WORKORDER_WALKER,
                                    )
                                ) {
                                    updateWoReq.body.approval_role_ids = approvalRoleIds
                                }

                                if (customPrice !== null) {
                                    updateWoReq.body.custom_price = customPrice
                                }
                                updateWorkorder(updateWoReq).then(() => {
                                    onClose()
                                    toast.success('Workorder Updated!')
                                })
                            }}
                        >
                            Save
                        </Button>
                    </Container>

                    <WorkorderTransitionLogPopver
                        open={popOverOpen}
                        anchorEl={anchorEl}
                        // anchorOrigin={{
                        //     horizontal: 'left',
                        //     vertical: 'bottom',
                        // }}
                        transitionLogs={workorder.transition_logs}
                        closePopover={closePopover}
                        workorder={workorder}
                    />
                </>
            )}
        </SideDrawer>
    )
}
